import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store/index';

// Access

const ACCESS = Object.freeze({
  USER: 'user',
  GUEST: 'guest',
});

// Paths

export const paths = Object.freeze({
  admin: '/admin',
  home: '/',
  findFace: '/find-face',
  temperature: '/temperature',
  signIn: '/signin',
  signUp: '/signup',
  personal: '/personal',
  personalProfile: '/personal-profile',
  personalBalance: '/personal-balance',
  personalMeter: '/personal-meter',
  personalAccounts: '/personal-accounts',
  personalAddAccount: '/personal-add-account',
  personalFindAccount: '/personal-find-account',
  qr: '/qr',
  guestBalance: '/guest-balance',
  guestAccount: '/guest-account',
  guestMeter: '/guest-meter',
  confirm: '/confirm',
  pay: '/pay',
  feedback: '/feedback',
  audioInstruction: '/audio-instruction',
  audioRecording: '/audio-recording',
  audioSend: '/audio-send',
  videoInstruction: '/video-instruction',
  videoRecording: '/video-recording',
  videoSend: '/video-send',
  photoTake: '/photo-take',
  photoSend: '/photo-send',
});

// Routes

export const routes = [
  {
    path: paths.admin,
    name: 'Admin',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/Admin.vue'),
    meta: {
      back: -1,
      footer: true,
    },
  },
  {
    path: paths.home,
    name: 'Home',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/Home.vue'),
    meta: {
      back: false,
      footer: true,
    },
    props: true,
  },
  {
    path: paths.findFace,
    name: 'FindFace',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/FindFace.vue'),
    meta: {
      back: false,
      footer: false,
    },
  },
  {
    path: paths.temperature,
    name: 'Temperature',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/Temperature.vue'),
    meta: {
      back: false,
      footer: true,
    },
  },
  {
    path: paths.signIn,
    name: 'SignIn',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/SignIn.vue'),
    meta: {
      back: paths.home,
      footer: true,
      access: ACCESS.GUEST,
    },
  },
  {
    path: paths.signUp,
    name: 'SignUp',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/SignUp.vue'),
    meta: {
      back: paths.home,
      footer: true,
      access: ACCESS.GUEST,
    },
  },
  {
    path: paths.personal,
    name: 'Personal',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/Personal.vue'),
    meta: {
      back: false,
      footer: true,
      access: ACCESS.USER,
    },
  },
  {
    path: paths.personalProfile,
    name: 'PersonalProfile',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/PersonalProfile.vue'),
    meta: {
      back: paths.personal,
      footer: true,
      access: ACCESS.USER,
    },
  },
  {
    path: paths.personalBalance,
    name: 'PersonalBalance',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/PersonalBalance.vue'),
    meta: {
      back: paths.personal,
      footer: true,
      access: ACCESS.USER,
    },
  },
  {
    path: paths.personalMeter,
    name: 'PersonalMeter',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/PersonalMeter.vue'),
    meta: {
      back: paths.personal,
      footer: true,
      access: ACCESS.USER,
    },
  },
  {
    path: paths.personalAccounts,
    name: 'PersonalAccounts',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/PersonalAccounts.vue'),
    meta: {
      back: paths.personal,
      footer: true,
      access: ACCESS.USER,
    },
  },
  {
    path: paths.personalAddAccount,
    name: 'PersonalAddAccount',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/PersonalAddAccount.vue'),
    meta: {
      back: paths.personalAccounts,
      footer: true,
      access: ACCESS.USER,
    },
  },
  {
    path: paths.personalFindAccount,
    name: 'PersonalFindAccount',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/PersonalFindAccount.vue'),
    meta: {
      back: paths.personalAccounts,
      footer: true,
      access: ACCESS.USER,
    },
  },
  {
    path: paths.qr,
    name: 'QR',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/QR.vue'),
    meta: {
      back: paths.home,
      footer: true,
    },
  },
  {
    path: paths.guestBalance,
    name: 'GuestBalance',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/GuestBalance.vue'),
    meta: {
      back: paths.qr,
      footer: true,
    },
  },
  {
    path: paths.guestAccount,
    name: 'GuestAccount',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/GuestAccount.vue'),
    meta: {
      back: paths.home,
      footer: true,
    },
  },
  {
    path: paths.guestMeter,
    name: 'GuestMeter',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/GuestMeter.vue'),
    meta: {
      back: paths.guestAccount,
      footer: true,
    },
  },
  {
    path: paths.confirm,
    name: 'Confirm',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/Confirm.vue'),
    meta: {
      back: -1,
      footer: true,
    },
  },
  {
    path: paths.pay,
    name: 'Pay',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/Pay.vue'),
    meta: {
      back: -2,
      footer: true,
    },
  },
  {
    path: paths.feedback,
    name: 'Feedback',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/Feedback.vue'),
    meta: {
      back: paths.home,
      footer: true,
    },
  },
  {
    path: paths.audioInstruction,
    name: 'AudioInstruction',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/AudioInstruction.vue'),
    meta: {
      back: paths.feedback,
      footer: true,
    },
  },
  {
    path: paths.audioRecording,
    name: 'AudioRecording',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/AudioRecording.vue'),
    meta: {
      back: paths.audioInstruction,
      footer: true,
    },
  },
  {
    path: paths.audioSend,
    name: 'AudioSend',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/AudioSend.vue'),
    meta: {
      back: paths.audioInstruction,
      footer: true,
    },
  },
  {
    path: paths.videoInstruction,
    name: 'VideoInstruction',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/VideoInstruction.vue'),
    meta: {
      back: paths.feedback,
      footer: true,
    },
  },
  {
    path: paths.videoRecording,
    name: 'VideoRecording',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/VideoRecording.vue'),
    meta: {
      back: paths.videoInstruction,
      footer: false,
    },
  },
  {
    path: paths.videoSend,
    name: 'VideoSend',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/VideoSend.vue'),
    meta: {
      back: paths.videoInstruction,
      footer: true,
    },
  },
  {
    path: paths.photoTake,
    name: 'PhotoTake',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/PhotoTake.vue'),
    meta: {
      back: paths.home,
      footer: false,
    },
  },
  {
    path: paths.photoSend,
    name: 'PhotoSend',
    component: () => import(/* webpackChunkName: "screens" */ '@/screens/PhotoSend.vue'),
    meta: {
      back: paths.photoTake,
      footer: true,
    },
  },
];

// Router

Vue.use(VueRouter);

const router = new VueRouter({
  linkActiveClass: '_active',
  linkExactActiveClass: '_active_exact',
  paths,
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

router.beforeEach((to, from, next) => {
  if (to.matched.some((record) => record.meta.access)) {
    const isAuthenticated = store.getters['personal/isAuthenticated'];
    const access = isAuthenticated ? ACCESS.USER : ACCESS.GUEST;
    const redirect = isAuthenticated ? paths.personal : paths.signIn;
    const hasAccess = to.meta.access === access;

    if (hasAccess) {
      next();
    } else {
      next({ path: redirect });
    }
  } else {
    next();
  }
});

export default router;
